import { ColorOptions } from '@df/ui/atomic/atomic-options';
import { NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { NPageFooter } from './page-footer.interface';

export const PAGE_FOOTER_CONFIG: Omit<NICmsContentComponent.IMeta<NICmsContentComponent.IComponent>, 'type'> = {
    id: NPageFooter.BLOCK_ID,
    onDemandOnly: true,
    block: {
        url: NPageFooter.BLOCK_ID,
        scope: 'website',
        autoload: 'prerender'
    },
    name: 'Page footer',
    description: 'Page footer component',
    fields: [
        // general
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-general',
            title: 'General'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'links',
            title: 'Bottom Links',
            itemName: 'Item',
            itemNameField: 'text',
            defaultSize: 3,
            fields: [
                {
                    key: 'text',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Copy',
                    default: 'Some link'
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link'
                }
            ]
        },
        /**
         * Desktop Columns
         */
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'columns',
            title: 'Columns',
            description: 'Columns for desktop',
            itemName: 'Column',
            itemNameField: 'header',
            maxSize: 2,
            complex: true,
            fields: [
                <NICmsContentField.IField>{
                    key: 'header',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Header',
                    required: true
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'links',
                    title: 'Links',
                    itemName: 'Link',
                    itemNameField: 'text',
                    fields: [
                        <NICmsContentField.IField>{
                            key: 'text',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Text',
                            required: true
                        },
                        <NICmsContentField.IField>{
                            key: 'link',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Link'
                        },
                        <NICmsContentField.IRepeaterField>{
                            type: NICmsContentField.EFieldType.repeater,
                            key: 'tabLinks',
                            title: 'Links',
                            itemName: 'Link',
                            itemNameField: 'text',
                            fields: [
                                <NICmsContentField.IField>{
                                    key: 'text',
                                    type: NICmsContentField.EFieldType.text,
                                    title: 'Text',
                                    required: true
                                },
                                <NICmsContentField.IField>{
                                    key: 'link',
                                    type: NICmsContentField.EFieldType.text,
                                    title: 'Link'
                                }
                            ],
                            default: []
                        }
                    ],
                    default: []
                }
            ],
            default: []
        },
        /**
         * Mobile Columns
         */
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'columnsMobile',
            title: 'Columns mobile',
            itemName: 'Column',
            itemNameField: 'header',
            complex: true,
            minSize: 0,
            fields: [
                <NICmsContentField.IField>{
                    key: 'header',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Header',
                    required: true
                },
                <NICmsContentField.IField>{
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'icon',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Icon',
                    required: true,
                    options: [
                        { key: 'login', title: 'Login' },
                        { key: 'contact', title: 'Contact' },
                        { key: 'question', title: 'Question' },
                        { key: 'locator', title: 'Locator' },
                        { key: 'add-account', title: 'Add Account' },
                        { key: 'shop', title: 'Shop' },
                        { key: 'information', title: 'Information' }
                    ]
                },
                <NICmsContentField.IField>{
                    key: 'accordionBackgroundColor',
                    type: NICmsContentField.EFieldType.colorPicker,
                    title: 'Accordion Background Color',
                    colorPreview: true
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'links',
                    title: 'Links',
                    itemName: 'Link',
                    itemNameField: 'text',
                    minSize: 0,
                    fields: [
                        <NICmsContentField.IField>{
                            key: 'text',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Text',
                            required: true
                        },
                        <NICmsContentField.IField>{
                            key: 'link',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Link'
                        }
                    ],
                    default: []
                }
            ],
            default: []
        },
        /**
         * Newsletter
         */
        {
            type: NICmsContentField.EFieldType.label,
            key: 'newsletter',
            title: 'Newsletter'
        },
        <NICmsContentField.IField>{
            key: 'newsletterHeader',
            type: NICmsContentField.EFieldType.text,
            title: 'Newsletter header',
            default: 'Lorem ipsum',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'newsletterHeaderImage',
            type: NICmsContentField.EFieldType.image,
            title: 'Newsletter header image'
        },
        <NICmsContentField.IField>{
            key: 'newsletterCopy',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Newsletter copy'
        },
        <NICmsContentField.IField>{
            key: 'newsletterTermsCopy',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Newsletter Terms copy',
            default: 'Lorem ipsum dolor sit amet, consectetur adipiscing eli ut T&Cs',
            required: true
        },
        <NICmsContentField.IChoiceField>{
            key: 'newsletterHeaderColor',
            type: NICmsContentField.EFieldType.choice,
            title: 'Header font color',
            options: ColorOptions,
            default: 'col-w',
            colorPreview: true
        },
        <NICmsContentField.IChoiceField>{
            key: 'newsletterCopyColor',
            type: NICmsContentField.EFieldType.choice,
            title: 'Copy font color',
            options: ColorOptions,
            default: 'col-w',
            colorPreview: true
        },
        <NICmsContentField.IField>{
            key: 'newsletterBackgroundColor',
            type: NICmsContentField.EFieldType.colorPicker,
            title: 'Background Color',
            colorPreview: true
        },
        <NICmsContentField.IField>{
            key: 'reviewsImage',
            type: NICmsContentField.EFieldType.image,
            title: 'Reviews image'
        },
        <NICmsContentField.ITextField>{
            key: 'reviewsLink',
            type: NICmsContentField.EFieldType.text,
            title: 'Reviews image link'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'socialIcons',
            title: 'Social Icons',
            itemName: 'Icon',
            itemNameField: 'icon',
            fields: [
                <NICmsContentField.IChoiceField>{
                    key: 'service',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Service',
                    required: true,
                    options: [
                        { key: 'facebook', title: 'Facebook' },
                        { key: 'twitter', title: 'Twitter' },
                        { key: 'instagram', title: 'Instagram' },
                        { key: 'pinterest', title: 'Pinterest' },
                        { key: 'tiktok', title: 'Tiktok' }
                    ]
                },
                <NICmsContentField.IField>{
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link'
                },
                <NICmsContentField.IField>{
                    key: 'title',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Title',
                    description: 'For ARIA label'
                }
            ]
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'sectionBackgroundColor',
            title: 'Background Color'
        },
        <NICmsContentField.IField>{
            key: 'backgroundColor',
            type: NICmsContentField.EFieldType.colorPicker,
            title: 'Background Color',
            colorPreview: true,
            default: '#fff'
        }
    ]
};
